/**
 * TRM API Client
 * Extends the existing BaseAPIService for TRM modules
 */

import { BaseAPIService } from '../../../../services/baseapiservice';
import type { ApiResponse, ErrorResponse } from '../types/common.types';

/**
 * TRM API Client extending BaseAPIService
 */
export class TRMApiClient extends BaseAPIService {
  private modulePath: string;

  constructor(modulePath: string) {
    super();
    this.modulePath = modulePath;
  }

  /**
   * Build full URL with module path
   */
  private buildUrl(endpoint: string): string {
    return `${this.modulePath}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  }

  /**
   * GET request (overridden to use module path)
   */
  async getFromModule<T = any>(endpoint: string, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await super.get(this.buildUrl(endpoint), config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  /**
   * POST request (overridden to use module path)
   */
  async postToModule<T = any>(endpoint: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await super.post(this.buildUrl(endpoint), data, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  /**
   * PUT request (overridden to use module path)
   */
  async putToModule<T = any>(endpoint: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await super.put(this.buildUrl(endpoint), data, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  /**
   * PATCH request (overridden to use module path)
   */
  async patchToModule<T = any>(endpoint: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await super.patch(this.buildUrl(endpoint), data, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  /**
   * DELETE request (overridden to use module path)
   */
  async deleteFromModule<T = any>(endpoint: string, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await super.delete(this.buildUrl(endpoint), config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  /**
   * File upload
   */
  async upload<T = any>(endpoint: string, file: File, fieldName: string = 'file'): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      const response = await super.post(this.buildUrl(endpoint), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  /**
   * Download file
   */
  async download(endpoint: string, filename?: string): Promise<void> {
    try {
      const response = await this.api.get(this.buildUrl(endpoint), {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  /**
   * Error handler
   */
  private handleError(error: any): never {
    if (error.response) {
      const { status, data } = error.response;
      const errorResponse: ErrorResponse = {
        status: data.status || status,
        message: data.message || 'An error occurred',
        errors: data.errors || {},
      };
      throw errorResponse;
    } else if (error.request) {
      throw {
        status: 0,
        message: 'Network error. Please check your connection.',
        errors: {},
      } as ErrorResponse;
    } else {
      throw {
        status: 0,
        message: error.message || 'An unexpected error occurred',
        errors: {},
      } as ErrorResponse;
    }
  }
}

// Create and export TRM API client instances
export const trainingApiClient = new TRMApiClient('training');
export const recruitmentApiClient = new TRMApiClient('recruitment');
export const jobMelaApiClient = new TRMApiClient('jobmela');
export const sharedApiClient = new TRMApiClient('shared');
export const dsGaApiClient = new TRMApiClient('ds-ga');

// Export convenience methods (maintaining backward compatibility)
export const trainingApi = {
  get: (url: string, config?: any) => trainingApiClient.getFromModule(url, config),
  post: (url: string, data?: any, config?: any) => trainingApiClient.postToModule(url, data, config),
  put: (url: string, data?: any, config?: any) => trainingApiClient.putToModule(url, data, config),
  delete: (url: string, config?: any) => trainingApiClient.deleteFromModule(url, config),
};

export const recruitmentApi = {
  get: (url: string, config?: any) => recruitmentApiClient.getFromModule(url, config),
  post: (url: string, data?: any, config?: any) => recruitmentApiClient.postToModule(url, data, config),
  put: (url: string, data?: any, config?: any) => recruitmentApiClient.putToModule(url, data, config),
  delete: (url: string, config?: any) => recruitmentApiClient.deleteFromModule(url, config),
};

export const jobMelaApi = {
  get: (url: string, config?: any) => jobMelaApiClient.getFromModule(url, config),
  post: (url: string, data?: any, config?: any) => jobMelaApiClient.postToModule(url, data, config),
  put: (url: string, data?: any, config?: any) => jobMelaApiClient.putToModule(url, data, config),
  delete: (url: string, config?: any) => jobMelaApiClient.deleteFromModule(url, config),
};

// Export default
export default TRMApiClient;
